import { AllergenImages } from "../../app/constants/allergensImages";
import { subStepType } from "../../app/recipePage/recipePage.constants";

export const getWeighingScaleValue = (
    conversionArray,
    conversionUnit,
    value,
    defaultUnit,
    convert
) => {
    if (conversionArray && convert) {
        if (conversionUnit === defaultUnit) {
            return {
                value: `${value} ${defaultUnit}`,
                valueNumberType: Number(value),
                displayBtn: true,
            }
        }
        if (conversionUnit !== "g") {
            const conversionFactor = conversionArray[0].factor;
            const convertedVal = Math.ceil(value / Number(conversionFactor));
            return {
                value: `${convertedVal} ${conversionUnit}`,
                valueNumberType: Number(convertedVal),
                defaultVal: `${value} ${defaultUnit}`,
                displayBtn: false,
            };
        }
        const conversionFactor = conversionArray.find((i: any) => i.unit === conversionUnit)?.factor;
        const convertedVal = Math.ceil(value * Number(conversionFactor));
        return convertedVal && convertedVal > 1
            ? {
                value: `${convertedVal} ${conversionUnit}`,
                valueNumberType: Number(convertedVal),
                defaultVal: `${value} ${defaultUnit}`,
                displayBtn: true,
            }
            : {
                value: `${value} ${defaultUnit}`,
                valueNumberType: Number(value),
                displayBtn: false,
            };
    }
    return {
        value: `${value} ${defaultUnit}`,
        valueNumberType: Number(value),
        displayBtn: defaultUnit === "g",
    };
};

export const getDefaultIngredients = (items) => {
    const itemsWithAlternate = items?.ingredients;
    return itemsWithAlternate
        ? itemsWithAlternate.find((i) => i?.default)
        : items;
};

export const getAllAllergens = (ingredients) => {
    if (ingredients.length > 0) {
        const allAllergens = ingredients.reduce((prev, next) => {
            const { allergens } = next;
            if (allergens) return [...prev, ...allergens];
            else return prev;
        }, []);
        const uniqueAllergens = allAllergens.reduce((prev, next) => {
            if (!prev.includes(next)) return [...prev, next];
            else return prev;
        }, []);
        return uniqueAllergens;
    }
    return [];
};
export const getAllAllergensImages = (ingredients) => {
    if (ingredients.length > 0) {
        const allAllergens = ingredients.reduce((prev, next) => {
            const { allergens } = next;
            if (allergens) return [...prev, ...allergens];
            else return prev;
        }, []);
        const AllergenImagesUrl = allAllergens.reduce((prev, next) => {
            return [...prev, AllergenImages[next]];
        }, []);
        return AllergenImagesUrl;
    }
    return [];
};

export const isProcessed = (tag) => tag.toLowerCase() === "processed"

const getTag = (tags) => {
    if (tags) {
        if (tags.includes("vegetables")) return "vegetables";
        else if (tags.includes("powder")) return "powder";
        else if (tags.includes("whole")) return "whole";
        else if (tags.includes("spices")) return "spices";
        else if (tags.includes("processed")) return "";
    }
    return "pantry";
};

const createAlternateIngredients = (defaultIngredient, ingredients) => {
    const ingredient = ingredients[defaultIngredient.ingredientId];
    return {
        displayName: ingredient?.displayName,
        id: defaultIngredient.ingredientId,
        image: ingredient?.image,
    };
};

export const transformIngredientsToObject = (ingredients) => {
    if (!Array.isArray(ingredients)) {
      return {};
    }
  
    return ingredients.reduce((result, ingredient) => {
      result[ingredient.id] = {
        ...ingredient,
        preps: ingredient.preps?.reduce((prepResult, prep) => {
          prepResult[prep.prepId] = prep;
          return prepResult;
        }, {}) || {},
      };
      return result;
    }, {});
  };
  

export const getIngredientsData = (item, ingredients) => {
    if (ingredients) {
        const defaultIngredient = getDefaultIngredients(item);
        const ingredient = ingredients[defaultIngredient.ingredientId];
        const prepId = defaultIngredient?.prep?.prepId;
        const prep =
            prepId && ingredient?.preps ? ingredient.preps[prepId] : null;
        const qty =
            defaultIngredient?.overruledAmount?.qty ||
            defaultIngredient?.amount?.qty;
        const unit =
            defaultIngredient?.overruledAmount?.unit ||
            defaultIngredient?.amount?.unit;
        const tags = ingredient?.tags || [];

        let conversionUnit = "g";
        let convert = false;
        if (ingredients[defaultIngredient?.ingredientId]) {
            const { defaultWeightUnit, defaultUnitTriggerPoint, conversion } =
                ingredients[defaultIngredient?.ingredientId];
            const factor = conversion[0].factor;
            if (defaultWeightUnit === unit) {
                if (qty > defaultUnitTriggerPoint && defaultUnitTriggerPoint !== 0) {
                    convert = true;
                }
            } else if (qty < Number(factor) * defaultUnitTriggerPoint) {
                convert = true;
                conversionUnit = defaultWeightUnit;
            }
        }

        return {
            id: defaultIngredient.ingredientId,
            displayName: ingredient?.displayName,
            prep,
            image: ingredient?.image,
            unit: defaultIngredient?.amount?.unit,
            tags,
            critical: defaultIngredient?.critical
                ? defaultIngredient.critical
                : false,
            approx: defaultIngredient?.approx,
            isNewIngredient: false,
            ingredientDetail: defaultIngredient?.ingredientDetail,
            allergens: ingredient?.allergens,
            nutrientInfo: ingredient?.nutrientInfo,
            defaultWeightUnit: ingredient?.defaultWeightUnit,
            secondaryUnit: ingredient?.secondaryUnit,
            alternateIngredients:
                item?.ingredients && item.ingredients.length > 1
                    ? item.ingredients.map((i) =>
                        createAlternateIngredients(i, ingredients)
                    )
                    : null,
            alternateNames: ingredient?.alternateNames,
            amount: { qty: defaultIngredient?.amount?.qty, unit: defaultIngredient?.amount?.unit },
            defaultWeight: getWeighingScaleValue(
                ingredient?.conversion,
                conversionUnit,
                defaultIngredient?.amount?.qty,
                defaultIngredient?.amount?.unit,
                convert
            ),
            weighScaleAmount: getWeighingScaleValue(
                ingredient?.conversion,
                conversionUnit,
                qty,
                unit,
                convert
            ),
            factor: Number(ingredient?.conversion[0].factor),
        };
    }
    return {};
};

export const getAddedIngredients = (ingredient, item) => {
    const { defaultWeight, weighScaleAmount } = ingredient;
    const aggregateDefaultWeight =
        defaultWeight?.valueNumberType + item?.defaultWeight?.valueNumberType;
    const aggregateWeighScale =
        weighScaleAmount?.valueNumberType +
        item?.weighScaleAmount?.valueNumberType;
    const weightUnit = defaultWeight.value.split(" ")[1];
    const addedItem = {
        ...ingredient,
        defaultWeight: {
            ...defaultWeight,
            value: `${aggregateDefaultWeight.toString()} ${weightUnit}`,
            valueNumberType: aggregateDefaultWeight,
        },
        weighScaleAmount: {
            ...weighScaleAmount,
            value: `${aggregateWeighScale.toString()} ${weightUnit}`,
            valueNumberType: aggregateWeighScale,
        },
    };
    return addedItem;
};

export const getSortedIngredients = (items) => {
    let initialData = {
        vegetables: [],
        spices: { whole: [], powder: [] },
        pantry: [],
    };

    items.forEach((i) => {
        switch (getTag(i?.tags)) {
            case "vegetables": {
                const ingredient = initialData.vegetables.find(
                    (j) => j.id === i.id
                );
                if (!ingredient)
                    initialData.vegetables = [...initialData.vegetables, i];
                else if (ingredient) {
                    initialData.vegetables = [
                        ...initialData.vegetables.filter((r) => r.id !== i.id),
                        getAddedIngredients(ingredient, i),
                    ];
                }
                break;
            }
            case "spices":
            case "powder": {
                const ingredient = initialData.spices.powder.find(
                    (j) => j.id === i.id
                );
                if (!ingredient)
                    initialData.spices = {
                        ...initialData.spices,
                        powder: [...initialData.spices.powder, i],
                    };
                else if (ingredient) {
                    initialData.spices = {
                        ...initialData.spices,
                        powder: [
                            ...initialData.spices.powder.filter(
                                (r) => r.id !== i.id
                            ),
                            getAddedIngredients(ingredient, i),
                        ],
                    };
                }
                break;
            }
            case "whole": {
                const ingredient = initialData.spices.whole.find(
                    (j) => j.id === i.id
                );
                if (!ingredient)
                    initialData.spices = {
                        ...initialData.spices,
                        whole: [...initialData.spices.whole, i],
                    };
                else if (ingredient) {
                    const ingredient = initialData.spices.whole.find(
                        (j) => j.id === i.id
                    );
                    initialData.spices = {
                        ...initialData.spices,
                        whole: [
                            ...initialData.spices.whole.filter(
                                (r) => r.id !== i.id
                            ),
                            getAddedIngredients(ingredient, i),
                        ],
                    };
                }
                break;
            }
            case "pantry": {
                const ingredient = initialData.pantry.find(
                    (j) => j.id === i.id
                );
                if (!ingredient)
                    initialData.pantry = [...initialData.pantry, i];
                else if (ingredient) {
                    initialData.pantry = [
                        ...initialData.pantry.filter((r) => r.id !== i.id),
                        getAddedIngredients(ingredient, i),
                    ];
                }
                break;
            }
            default:
                return null;
        }
    });

    return initialData;
};

export const getNutrientDifference = (newNutrientInfo, oldNutrientInfo) => {
    const defaultNutrientInfo = { calorie: 0, fat: 0, protein: 0, carbs: 0 };

    const safeNewNutrientInfo = { ...defaultNutrientInfo, ...newNutrientInfo };
    const safeOldNutrientInfo = { ...defaultNutrientInfo, ...oldNutrientInfo };

    return {
        calorie: safeNewNutrientInfo.calorie - safeOldNutrientInfo.calorie,
        fat: safeNewNutrientInfo.fat - safeOldNutrientInfo.fat,
        protein: safeNewNutrientInfo.protein - safeOldNutrientInfo.protein,
        carbs: safeNewNutrientInfo.carbs - safeOldNutrientInfo.carbs,
    };
};


export const getTotalMacros = (items) => {
    if (items.length > 0) {
        const macro = items.reduce(
            (prev, next) => {
                let qty =
                    next.weighScaleAmount.valueNumberType ||
                    next.defaultWeight.valueNumberType;
                if (!next?.weighScaleAmount?.displayBtn) {
                    qty = qty * next?.factor
                }
                if (next.tags.includes("processed")) {
                    return prev;
                } else {
                    const calorie =
                        Math.round((next?.nutrientInfo?.calorie * qty) / 100) ||
                        0;
                    const fat =
                        Math.round((next?.nutrientInfo?.fat * qty) / 100) || 0;
                    const protein =
                        Math.round((next?.nutrientInfo?.protein * qty) / 100) ||
                        0;
                    const carbs =
                        Math.round((next?.nutrientInfo?.carbs * qty) / 100) ||
                        0;
                    return {
                        calorie: calorie + prev.calorie,
                        fat: fat + prev.fat,
                        protein: protein + prev.protein,
                        carbs: carbs + prev.carbs,
                    };
                }
            },
            {
                calorie: 0,
                fat: 0,
                protein: 0,
                carbs: 0,
            }
        );
        return macro;
    }
    return null;
};

export const getNutrientInfo = (oldRecipe, ingredients, newSubStep, oldSubStep) => {
    const newSubStepIngredientData = getAllRecipeIngredientsSubStep(newSubStep, ingredients);
    const oldSubStepIngredientData = getAllRecipeIngredientsSubStep(oldSubStep, ingredients);
    const newRecipeMacros = getTotalMacros(newSubStepIngredientData) || { calorie: 0, fat: 0, protein: 0, carbs: 0 };
    const oldRecipeMacros = getTotalMacros(oldSubStepIngredientData) || { calorie: 0, fat: 0, protein: 0, carbs: 0 };

    const nutrientDifference = getNutrientDifference(newRecipeMacros, oldRecipeMacros);
    if (oldRecipe?.Recipe_Variations?.[0]?.metadata?.nutritionalValues) {
        const currentNutrientInfo = oldRecipe.Recipe_Variations[0].metadata.nutritionalValues;

        const updatedNutrientInfo = {
            calorie: (currentNutrientInfo.calorie || 0) + nutrientDifference.calorie,
            fat: (currentNutrientInfo.fat || 0) + nutrientDifference.fat,
            protein: (currentNutrientInfo.protein || 0) + nutrientDifference.protein,
            carbs: (currentNutrientInfo.carbs || 0) + nutrientDifference.carbs,
        };

        return updatedNutrientInfo;
    }

    return nutrientDifference;
};


export const getCustomIngredientsData = (item) => {
    const ingredient = item.ingredients[0]
    const qty = ingredient.quantity;
    const { unit } = ingredient;
    const conversionUnit = "g";
    const convert = false;
    return {
        id: ingredient?.id ?? "",
        displayName: ingredient.name,
        prep: null,
        image: false,
        type: 'custom',
        unit,
        tags: [],
        critical: false,
        approx: undefined,
        ingredientDetail: undefined,
        allergens: [],
        nutrientInfo: { fat: '', carbs: '', calorie: '', protein: '' },
        alternateNames: [],
        alternateIngredients: null,
        defaultWeight: getWeighingScaleValue(
            { unit, factor: '0' },
            conversionUnit,
            qty,
            unit,
            convert
        ),
        weighScaleAmount: getWeighingScaleValue(
            { unit, factor: '0' },
            conversionUnit,
            qty,
            unit,
            convert
        ),
        factor: 0,
    };
};

export const getAllRecipeIngredients = (stepsArray, ingredients) => {
    if (stepsArray && ingredients) {
        const filteredSteps = stepsArray.reduce((prev, step) => {
            const subStepIngredients = step.subSteps.reduce((p, subStep) => {
                const ingredientsType = [subStepType.INGREDIENTS, subStepType.CUSTOM_INSTRUCTION]
                if (
                    ingredientsType.includes(subStep.type)
                ) {
                    const items = subStep?.parameters?.items;

                    const data = items.map((i) => {
                        const ingredientData = subStep.type === subStepType.INGREDIENTS ?
                            getIngredientsData(
                                i,
                                ingredients
                            ) :
                            getCustomIngredientsData(
                                i
                            );
                        return {
                            subStepId: subStep?.id,
                            subStepUId: subStep?.subStepUId,
                            subStepName: subStep?.name,
                            ingredientsTitle: subStep?.ingredientsTitle,
                            ...ingredientData,
                        };
                    });
                    const filteredData = data.filter(
                        (i) => !i.tags.find(isProcessed)
                    );
                    return [...p, ...filteredData];
                }
                return p;
            }, []);
            return [...prev, ...subStepIngredients];
        }, [])
        return filteredSteps
    }
    return [];
};

export const getMissingIngredientIdsFromSubStep = (subStep, ingredients) => {
    if (!subStep?.parameters?.items) {
      return [];
    }
  
    const ingredientsMap = ingredients || {};
  
    const missingIds = [];
  
    for (const item of subStep.parameters.items) {
      if (!Array.isArray(item.ingredients)) {
        continue;
      }
      for (const ing of item.ingredients) {
        const { ingredientId } = ing;
        if (ingredientId && !ingredientsMap[ingredientId]) {
          missingIds.push(ingredientId);
        }
      }
    }
  
    return missingIds;
  };
  
  
  export const ensureIngredientsObject = async (
    ingredientsObject,
    newSubstep,
    fetchRecipeIngredients
  ) => {
    const missingIngredientIds = getMissingIngredientIdsFromSubStep(newSubstep, ingredientsObject);
  
    if (missingIngredientIds.length === 0) {
      return ingredientsObject;
    }
  
    const ingredientData = await fetchRecipeIngredients({
      variables: { _in: missingIngredientIds },
      skip: missingIngredientIds.length === 0,
    });
  
    const fetchedIngredientsObject = transformIngredientsToObject(ingredientData?.data?.Ingredients_DB);
  
    return { ...ingredientsObject, ...fetchedIngredientsObject };
  };
  

export const getAllRecipeIngredientsSubStep = (subStep, ingredients) => {
    if (subStep && ingredients) {
        const ingredientsType = [subStepType.INGREDIENTS, subStepType.CUSTOM_INSTRUCTION]
        if (ingredientsType.includes(subStep.type)) {
            const items = subStep?.parameters?.items;
            const data = items.map((i) => {
                const ingredientData = subStep.type === subStepType.INGREDIENTS ? getIngredientsData(i, ingredients) : getCustomIngredientsData(i);
                return {
                    subStepId: subStep?.id,
                    subStepUId: subStep?.subStepUId,
                    subStepName: subStep?.name,
                    ingredientsTitle: subStep?.ingredientsTitle,
                    ...ingredientData,
                };
            });
            const filteredData = data.filter(
                (i) => !i.tags.find(isProcessed)
            );
            return filteredData;
        }
    }
    return [];
};

export const changeDefaultIngredient = (
    recipeData,
    stepId,
    ingredientId,
    altBoxIndex
) => {
    const { steps } = recipeData;
    const { subSteps } = steps.find((i) => i?.id === stepId);
    let itemTracker = 0;
    const newSubStep = subSteps.map((i) => {
        if (
            i.type === "addIngredientsWithAlternate" ||
            i.type === "addIngredients" ||
            i.type === "criticalStep"
        ) {
            let newItems = [];
            i.parameters.items.map((j) => {
                if (
                    itemTracker === altBoxIndex &&
                    i.type === "addIngredientsWithAlternate"
                ) {
                    itemTracker = -100;
                    let newIngredients = j.ingredients.map((k) => {
                        if (k.ingredientId === ingredientId) {
                            return { ...k, default: true };
                        }
                        return { ...k, default: false };
                    });
                    newItems.push({ ingredients: newIngredients });
                    return;
                }
                itemTracker++;
                newItems.push({ ...j });
            });
            return { ...i, parameters: { items: newItems } };
        }
        return { ...i };
    });
    //testing again
    const newSteps = steps.map((i) => {
        if (i.id === stepId) return { ...i, subSteps: newSubStep };
        return i;
    });
    return { ...recipeData, steps: newSteps };
};

export function sortIngredients(ingredients) {
    const preferredUnitOrder = ["g", "ml", "tbsp", "tsp"];

    return [...ingredients].sort((a, b) => {
        const unitA = a.weightScaleValue.value.split(" ")[1] || ""; // extract unit or use empty string if none
        const unitB = b.weightScaleValue.value.split(" ")[1] || "";

        const rankA = preferredUnitOrder.indexOf(unitA);
        const rankB = preferredUnitOrder.indexOf(unitB);

        if (rankA !== -1 && rankB !== -1) {
            if (rankA === rankB) {
                return b.weightScaleValue.valueNumberType - a.weightScaleValue.valueNumberType;
            }
            return rankA - rankB;
        }

        if (rankA !== -1) return -1;
        if (rankB !== -1) return 1;

        if (unitA !== unitB) return unitA.localeCompare(unitB);

        return b.weightScaleValue.valueNumberType - a.weightScaleValue.valueNumberType;
    });
}

